import React, { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Loader2, MapPin, BookOpen, Workflow, Users, HandHeart, Building, Home, Lightbulb, Heart, Sparkles, Calendar, HelpCircle, ExternalLink, Award, Info, ClipboardList, ListOrdered } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import ReactMarkdown from "react-markdown";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  suggestions?: string[];
  navigationContext?: NavigationContext | null;
}

interface NavigationContext {
  title: string;
  summary: string;
  icon: string;
  type: 'page' | 'section';
  target: string;
}

interface NavigationTarget {
  target_type: 'page' | 'section';
  target_id: string;
  page_context: string | null;
  title: string;
  summary: string | null;
  icon: string | null;
}

const STORAGE_KEY = "chatPane_messages";
const STORAGE_LANG_KEY = "chatPane_language";

// Icon component mapping
const iconComponents: Record<string, React.ReactNode> = {
  BookOpen: <BookOpen size={18} />,
  Workflow: <Workflow size={18} />,
  Users: <Users size={18} />,
  HandHeart: <HandHeart size={18} />,
  Building: <Building size={18} />,
  Building2: <Building size={18} />,
  Home: <Home size={18} />,
  MapPin: <MapPin size={18} />,
  Lightbulb: <Lightbulb size={18} />,
  Heart: <Heart size={18} />,
  Sparkles: <Sparkles size={18} />,
  Calendar: <Calendar size={18} />,
  HelpCircle: <HelpCircle size={18} />,
  Award: <Award size={18} />,
  Info: <Info size={18} />,
  ClipboardList: <ClipboardList size={18} />,
  ListOrdered: <ListOrdered size={18} />,
};

const getIconComponent = (iconName: string) => {
  return iconComponents[iconName] || <MapPin size={18} />;
};

const ChatPane: React.FC = () => {
  const { setOpen, open, setOpenMobile, openMobile, isMobile } = useSidebar();
  const location = useLocation();
  
  // Unified open/close handlers for desktop and mobile
  const isOpen = isMobile ? openMobile : open;
  const handleOpen = () => isMobile ? setOpenMobile(true) : setOpen(true);
  const handleClose = () => isMobile ? setOpenMobile(false) : setOpen(false);
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Dynamic navigation contexts from database
  const [navigationContexts, setNavigationContexts] = useState<Record<string, NavigationContext>>({});

  const [language, setLanguage] = useState<"en" | "es">(() => {
    const saved = localStorage.getItem(STORAGE_LANG_KEY);
    return (saved === "es" ? "es" : "en");
  });

  const translations = {
    en: {
      title: "AI Companion",
      placeholder: "Ask me anything...",
      thinking: "Thinking...",
      welcomeMessage: "Hello! I'm your AI assistant. How can I help you today?",
      errorMessage: "I'm having trouble connecting right now. Please try again in a moment.",
      clearChat: "Clear chat"
    },
    es: {
      title: "Asistente de IA",
      placeholder: "Pregúntame cualquier cosa...",
      thinking: "Pensando...",
      welcomeMessage: "¡Hola! Soy tu asistente de IA. ¿En qué puedo ayudarte hoy?",
      errorMessage: "Estoy teniendo problemas de conexión ahora. Por favor, inténtalo de nuevo en un momento.",
      clearChat: "Borrar chat"
    }
  };

  const t = translations[language];

  const getWelcomeMessage = (lang: "en" | "es"): ChatMessage => ({
    id: "welcome",
    role: "assistant",
    content: translations[lang].welcomeMessage,
    timestamp: Date.now()
  });

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch {
      // ignore parse errors
    }
    return [getWelcomeMessage(language)];
  });

  const [input, setInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch navigation targets from database
  useEffect(() => {
    const fetchNavTargets = async () => {
      const { data, error } = await supabase
        .from('navigation_targets')
        .select('*')
        .eq('is_active', true);
      
      if (error) {
        console.error('Error fetching navigation targets:', error);
        return;
      }

      const contexts: Record<string, NavigationContext> = {};
      data?.forEach((target) => {
        // Build the key for lookup
        let key: string;
        if (target.target_type === 'page') {
          key = target.target_id;
        } else if (target.page_context === 'home') {
          key = target.target_id;
        } else {
          // Cross-page section: "pagename:section-id"
          key = `${target.page_context}:${target.target_id}`;
        }
        
        contexts[key] = {
          title: target.title,
          summary: target.summary || '',
          icon: target.icon || 'MapPin',
          type: target.target_type as 'page' | 'section',
          target: key
        };
      });
      
      setNavigationContexts(contexts);
    };
    
    fetchNavTargets();
  }, []);

  // Persist messages to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  // Persist language preference
  useEffect(() => {
    localStorage.setItem(STORAGE_LANG_KEY, language);
  }, [language]);

  // Update welcome message when language changes (only if it's the only message)
  useEffect(() => {
    setMessages(prev => {
      if (prev.length === 1 && prev[0].id === "welcome") {
        return [getWelcomeMessage(language)];
      }
      return prev;
    });
  }, [language]);

  // Auto-scroll to newest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Listen for custom event to open the chat pane
  useEffect(() => {
    const handleOpenChat = () => {
      handleOpen();
    };
    window.addEventListener("openAIAssistant", handleOpenChat);
    return () => window.removeEventListener("openAIAssistant", handleOpenChat);
  }, [isMobile, setOpen, setOpenMobile]);

  // Listen for event with initial message (from hero input)
  const pendingMessageRef = useRef<string | null>(null);
  
  useEffect(() => {
    const handleOpenWithMessage = (event: CustomEvent<{ message: string }>) => {
      handleOpen();
      const messageToSend = event.detail?.message;
      if (messageToSend) {
        pendingMessageRef.current = messageToSend;
        setInput(messageToSend);
      }
    };
    
    window.addEventListener("openChatPaneWithMessage", handleOpenWithMessage as EventListener);
    return () => window.removeEventListener("openChatPaneWithMessage", handleOpenWithMessage as EventListener);
  }, [isMobile, setOpen, setOpenMobile]);

  // Auto-send pending message when panel is open and ready
  useEffect(() => {
    if (isOpen && pendingMessageRef.current && input === pendingMessageRef.current && !isProcessing) {
      pendingMessageRef.current = null;
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        handleSend();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [open, input, isProcessing]);

  const clearChat = () => {
    setMessages([getWelcomeMessage(language)]);
  };

  // Execute navigation with support for PAGE, SECTION, and PAGE_SECTION
  const executeNavigation = async (type: string, target: string) => {
    const currentPath = window.location.pathname;
    
    if (type === "PAGE") {
      navigate(`/${target}`);
    } else if (type === "PAGE_SECTION") {
      // Cross-page navigation: target is "pagename:section-id"
      const [page, section] = target.split(':');
      const targetPath = `/${page}`;
      
      if (currentPath !== targetPath) {
        navigate(targetPath);
        // Wait for page to load, then scroll
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else if (type === "SECTION") {
      // Check if this is a homepage section
      const homepageSections = Object.keys(navigationContexts).filter(key => {
        const ctx = navigationContexts[key];
        return ctx.type === 'section' && !key.includes(':');
      });
      
      if (homepageSections.includes(target) && currentPath !== "/") {
        navigate("/");
        // Wait for page to load, then scroll
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      const element = document.getElementById(target);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  // Execute action commands (Sandbox, Library features)
  const executeAction = async (actionType: string, payload: string) => {
    const currentPath = window.location.pathname;
    
    switch (actionType) {
      case "SANDBOX":
        if (currentPath !== "/sandbox") {
          navigate("/sandbox");
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        window.dispatchEvent(new CustomEvent("executeSandboxPrompt", { 
          detail: { prompt: payload } 
        }));
        break;
        
      case "LIBRARY_SEARCH":
        if (currentPath !== "/library") {
          navigate("/library");
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        window.dispatchEvent(new CustomEvent("executeLibrarySearch", { 
          detail: { query: payload } 
        }));
        break;
        
      case "LIBRARY_FILTER":
        if (currentPath !== "/library") {
          navigate("/library");
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        window.dispatchEvent(new CustomEvent("executeLibraryFilter", { 
          detail: { tagName: payload } 
        }));
        break;
        
      case "LIBRARY_CHAT":
        if (currentPath !== "/library") {
          navigate("/library");
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        window.dispatchEvent(new CustomEvent("executeLibraryChat", { 
          detail: { question: payload } 
        }));
        break;
    }
  };

  const handleSend = async () => {
    const textToSend = input.trim();
    if (!textToSend || isProcessing) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: textToSend,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke("event-assistant", {
        body: {
          messages: [
            ...messages.map(m => ({ role: m.role, content: m.content })),
            { role: "user", content: textToSend }
          ],
          language: language,
          currentPage: location.pathname
        }
      });

      if (error) throw error;

      const responseText = data.message || "I'm sorry, I couldn't generate a response.";

      // Parse navigation directives (including new PAGE_SECTION format)
      const navDirectiveRegex = /\[NAV:(SECTION|PAGE|PAGE_SECTION):([^\]]+)\]/g;
      const navMatches = [...responseText.matchAll(navDirectiveRegex)];

      // Parse action directives
      const actionDirectiveRegex = /\[ACTION:(SANDBOX|LIBRARY_SEARCH|LIBRARY_FILTER|LIBRARY_CHAT):([^\]]+)\]/g;
      const actionMatches = [...responseText.matchAll(actionDirectiveRegex)];

      // Parse suggestion directives
      const suggestDirectiveRegex = /\[SUGGEST:([^\]]+)\]/g;
      const suggestMatches = [...responseText.matchAll(suggestDirectiveRegex)];
      const suggestions = suggestMatches.map(match => match[1]);

      // Remove all directives from display
      const cleanResponse = responseText
        .replace(/\[NAV:(SECTION|PAGE|PAGE_SECTION):[^\]]+\]/g, "")
        .replace(/\[ACTION:(SANDBOX|LIBRARY_SEARCH|LIBRARY_FILTER|LIBRARY_CHAT):[^\]]+\]/g, "")
        .replace(/\[SUGGEST:[^\]]+\]/g, "")
        .trim();

      // Build navigation context for mobile
      let navContext: NavigationContext | null = null;
      if (isMobile && (navMatches.length > 0 || actionMatches.length > 0)) {
        const firstNav = navMatches[0];
        const firstAction = actionMatches[0];
        
        if (firstNav) {
          const [, type, target] = firstNav;
          let contextKey = target;
          
          // For PAGE_SECTION, target is already "page:section"
          if (type === 'PAGE_SECTION') {
            contextKey = target;
          }
          
          const context = navigationContexts[contextKey] || navigationContexts[target];
          if (context) {
            navContext = {
              ...context,
              type: type === 'PAGE' ? 'page' : 'section',
              target
            };
          } else {
            // Fallback for unknown targets
            navContext = {
              title: type === 'PAGE_SECTION' ? 'Navigating...' : target,
              summary: `Going to ${target}`,
              icon: 'MapPin',
              type: type === 'PAGE' ? 'page' : 'section',
              target
            };
          }
        } else if (firstAction) {
          const [, actionType] = firstAction;
          if (actionType === 'SANDBOX') {
            navContext = navigationContexts['sandbox'] || { 
              title: 'Solution Sandbox', 
              summary: 'Practice building AI solutions', 
              icon: 'Lightbulb', 
              type: 'page', 
              target: 'sandbox' 
            };
          } else if (actionType.startsWith('LIBRARY')) {
            navContext = navigationContexts['library'] || { 
              title: 'Resource Library', 
              summary: 'Browse AI tools and resources', 
              icon: 'BookOpen', 
              type: 'page', 
              target: 'library' 
            };
          }
        }
      }

      const aiMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: cleanResponse,
        timestamp: Date.now(),
        suggestions: suggestions.length > 0 ? suggestions : undefined,
        navigationContext: navContext
      };
      setMessages(prev => [...prev, aiMsg]);

      // Execute navigations after a short delay so user can read the response
      if (navMatches.length > 0 || actionMatches.length > 0) {
        setTimeout(async () => {
          // Execute navigation commands first
          for (const match of navMatches) {
            const [, type, target] = match;
            await executeNavigation(type, target);
          }
          
          // Then execute action commands
          for (const match of actionMatches) {
            const [, actionType, payload] = match;
            await executeAction(actionType, payload);
          }
        }, 600);
      }
    } catch (error) {
      console.error("Error calling assistant:", error);
      const errorMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: t.errorMessage,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle suggestion bubble click
  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    // Auto-submit after a short delay
    setTimeout(() => {
      const event = new Event('submit', { bubbles: true, cancelable: true });
      document.querySelector('form')?.dispatchEvent(event);
    }, 100);
  };

  return (
    <Sidebar side="left" collapsible="offcanvas" className="border-r border-slate-200 bg-white shadow-xl">
      <SidebarHeader className="border-b border-slate-200 p-4 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare size={20} className="text-primary" />
            <h3 className="font-semibold text-slate-800">{t.title}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Select value={language} onValueChange={(value: "en" | "es") => setLanguage(value)}>
              <SelectTrigger className="w-[100px] h-8 text-xs bg-white border-slate-200 text-slate-700" aria-label="Select language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="en" className="text-slate-700">English</SelectItem>
                <SelectItem value="es" className="text-slate-700">Español</SelectItem>
              </SelectContent>
            </Select>
            <button
              onClick={handleClose}
              className="text-slate-400 hover:text-slate-700 p-1"
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="flex-1 overflow-hidden bg-white">
        <ScrollArea className="h-full p-4">
          <div className="space-y-4 pb-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"} animate-fade-in`}
              >
                <div
                  className={`max-w-[85%] p-3 rounded-lg ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-slate-100 text-slate-800"
                  }`}
                >
                  {msg.role === "user" ? (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  ) : (
                    <div className="text-sm prose prose-sm max-w-none text-slate-800 prose-headings:text-slate-800 prose-p:text-slate-800 prose-li:text-slate-800 prose-strong:text-slate-800 prose-a:text-sky-600 hover:prose-a:text-sky-700 [&_ul>li::marker]:text-slate-500 [&_ol>li::marker]:text-slate-500 [&>p]:mb-3 [&>p:last-child]:mb-0 [&>ul]:my-2 [&>ol]:my-2 [&>li]:my-1 [&>h1]:my-3 [&>h2]:my-3 [&>h3]:my-3 [&>strong]:font-semibold">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
                
                {/* Navigation Context Card (mobile only) */}
                {msg.role === "assistant" && msg.navigationContext && isMobile && (
                  <div className="mt-3 w-full max-w-[85%] bg-primary/5 border border-primary/20 rounded-lg p-3">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-primary/10 text-primary rounded-lg shrink-0">
                        {getIconComponent(msg.navigationContext.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-800 text-sm">{msg.navigationContext.title}</h4>
                        <p className="text-xs text-slate-500 mt-0.5">{msg.navigationContext.summary}</p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="w-full mt-3 text-xs gap-2 border-primary/30 text-primary hover:bg-primary/10"
                      onClick={handleClose}
                    >
                      <ExternalLink size={14} />
                      {language === 'es' ? 'Cerrar chat para ver' : 'Close chat to view'}
                    </Button>
                  </div>
                )}
                
                {/* Suggestion Bubbles */}
                {msg.role === "assistant" && msg.suggestions && msg.suggestions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2 max-w-[95%]">
                    {msg.suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="text-xs bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-full hover:border-primary hover:text-primary transition-colors active:scale-95"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {isProcessing && (
              <div className="flex justify-start animate-fade-in">
                <div className="bg-slate-100 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">{t.thinking}</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </SidebarContent>

      <SidebarFooter className="border-t border-slate-200 p-4 bg-white">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t.placeholder}
            disabled={isProcessing}
            className="flex-grow bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
          />
          <Button type="submit" disabled={isProcessing || !input.trim()} size="icon" aria-label="Send message">
            <Send size={18} />
          </Button>
        </form>
        {messages.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearChat}
            className="mt-2 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 w-full"
          >
            {t.clearChat}
          </Button>
        )}
      </SidebarFooter>
    </Sidebar>
  );
};

export default ChatPane;
